import { useEligibilityCoupon } from '@/domains/loyalty/modules/CouponEligibility/hooks/useEligibilityCoupon';
import { getBestCoupon } from '@/domains/productDiscovery/CommercialAnimation/utils/getBestCoupon';
import type { Coupon } from '@/domains/productDiscovery/ProductPage/interfaces/Detail.common.model';
import { useBulkCouponValue } from '@/productDiscovery/ProductPage/common/modules/BulkPurchase/hooks/useBulkCouponValue';

export interface ProductCoupon {
  code: string;
  percent?: number;
  amount?: number;
  endTime?: string;
}

export interface ProductCouponBySellers {
  discountValue: number;
}

type CouponKind =
  | 'no-coupon'
  | 'simple'
  | 'bulk-purchase-by-mm'
  | 'bulk-purchase-by-seller';

interface CouponReturnValue<
  Kind extends CouponKind,
  CouponToUse extends
    | ProductCoupon
    | ProductCouponBySellers
    | undefined = undefined,
> {
  type: Kind;
  coupon: CouponToUse | undefined;
  otherCouponsAvailable: CouponKind[];
}

type UseProductCouponReturn =
  | CouponReturnValue<'no-coupon'>
  | CouponReturnValue<'simple', ProductCoupon>
  | CouponReturnValue<'bulk-purchase-by-seller', ProductCouponBySellers>;

const getBestCouponFromList = (
  coupons: Coupon[],
): ProductCoupon | undefined => {
  const bestCoupon = getBestCoupon(coupons);

  if (bestCoupon) {
    return {
      ...bestCoupon,
      amount: parseInt(bestCoupon.amount || '0', 10),
    };
  }

  return undefined;
};

export const useProductCoupon = (
  context: string,
  productId: number,
  quantity: number,
  unitPrice: number,
  coupons?: Coupon[],
): UseProductCouponReturn => {
  const { eligibilityCoupon } = useEligibilityCoupon();
  let couponToDisplay: ProductCoupon | undefined;

  if (coupons?.length) {
    couponToDisplay = getBestCouponFromList(coupons);
  } else if (['product page', 'A2C'].includes(context)) {
    couponToDisplay = eligibilityCoupon;
  }

  const bulkCouponValue = useBulkCouponValue(productId, quantity, unitPrice);

  if (bulkCouponValue !== undefined) {
    const otherCouponsAvailable = [
      couponToDisplay ? 'simple' : undefined,
    ].filter(Boolean) as CouponKind[];

    if (bulkCouponValue === 0) {
      return {
        type: 'bulk-purchase-by-seller',
        coupon: undefined,
        otherCouponsAvailable,
      };
    }

    return {
      coupon: {
        discountValue: bulkCouponValue,
      },
      type: 'bulk-purchase-by-seller',
      otherCouponsAvailable,
    };
  }

  if (couponToDisplay) {
    return {
      type: 'simple',
      coupon: couponToDisplay,
      otherCouponsAvailable: [],
    };
  }

  return {
    type: 'no-coupon',
    coupon: undefined,
    otherCouponsAvailable: [],
  };
};
